import { defineStore } from "pinia";
import io from "socket.io-client";
import { useAuthStore } from "./auth";
import { useNotificationStore } from "./notifications";
import { useGameStore } from "./game";
import { useGuildStore } from "./guild";
import { getApiBase } from "../utils/apiBase";

export const HISTORY_LIMIT = 100;
export const MAX_MESSAGE_LENGTH = 500;

const TABS = ["all", "world", "personal", "guild"];

function emptyUnread() {
  return { all: 0, world: 0, personal: 0, guild: 0 };
}

export function isPersonalMessage(msg) {
  return (
    msg.channel === "whisper" ||
    msg.channel === "system" ||
    msg.recipientName != null ||
    (msg.type === "system" && msg.recipient_id != null)
  );
}

export function isWorldMessage(msg) {
  return msg.channel === "world";
}

export function isGuildMessage(msg) {
  return msg.channel === "guild";
}

function tabsForMessage(msg) {
  const tabs = ["all"];
  if (isWorldMessage(msg)) tabs.push("world");
  if (isPersonalMessage(msg)) tabs.push("personal");
  if (isGuildMessage(msg)) tabs.push("guild");
  return tabs;
}

export const useChatStore = defineStore("chat", {
  state: () => ({
    socket: null,
    messages: [],
    isCollapsed: true,
    activeTab: "all",
    isConnected: false,
    unreadCount: 0,
    unreadByTab: emptyUnread(),
    historyLoaded: false,
  }),

  getters: {
    allMessages: (state) => state.messages,
    worldMessages: (state) => state.messages.filter(isWorldMessage),
    personalMessages: (state) => state.messages.filter(isPersonalMessage),
    guildMessages: (state) => state.messages.filter(isGuildMessage),
    visibleMessages(state) {
      if (state.activeTab === "world") return this.worldMessages;
      if (state.activeTab === "personal") return this.personalMessages;
      if (state.activeTab === "guild") return this.guildMessages;
      return this.allMessages;
    },
  },

  actions: {
    initSocket() {
      const authStore = useAuthStore();
      if (!authStore.user || this.socket) return;

      const baseUrl = getApiBase() || "http://localhost:3000";
      this.socket = io(baseUrl, {
        withCredentials: true,
      });

      this.socket.on("connect", () => {
        this.isConnected = true;
        this.fetchHistory();
      });

      this.socket.on("disconnect", () => {
        this.isConnected = false;
      });

      this.socket.on("new-message", (msg) => {
        this.addMessage(msg, { fromHistory: false });
      });

      this.socket.on("intel_updated", async () => {
        const gameStore = useGameStore();
        try {
          await Promise.all([
            gameStore.fetchWorldIntel(),
            gameStore.fetchScoutingMissions(),
            gameStore.fetchWorldMap(),
            gameStore.fetchPlayerResources(),
          ]);
        } catch {
          /* toast/chat already arrived; lists refresh on next visit */
        }
      });
    },

    disconnect() {
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
        this.isConnected = false;
        this.messages = [];
        this.unreadCount = 0;
        this.unreadByTab = emptyUnread();
        this.historyLoaded = false;
      }
    },

    fetchHistory() {
      if (!this.socket) return;
      this.historyLoaded = false;
      this.socket.emit("fetch-history", (response) => {
        if (response.success) {
          this.messages = response.messages;
          this.historyLoaded = true;
        }
      });
    },

    emitSocket(event, payload) {
      if (!this.socket) {
        return Promise.reject("Not connected");
      }
      return new Promise((resolve, reject) => {
        this.socket.emit(event, payload, (response) => {
          if (response?.success) {
            resolve(response.message);
          } else {
            reject(response?.error || "Failed to send message.");
          }
        });
      });
    },

    sendWhisper(messageText, recipientNickname) {
      const trimmed = (messageText || "").trim();
      if (!trimmed) {
        return Promise.reject("Message cannot be empty.");
      }
      if (trimmed.length > MAX_MESSAGE_LENGTH) {
        return Promise.reject(
          `Message too long (max ${MAX_MESSAGE_LENGTH} characters).`
        );
      }
      return this.emitSocket("send-message", {
        message: trimmed,
        channel: "whisper",
        recipientNickname,
      });
    },

    sendWorldMessage(messageText) {
      const trimmed = (messageText || "").trim();
      if (!trimmed) {
        return Promise.reject("Message cannot be empty.");
      }
      if (trimmed.length > MAX_MESSAGE_LENGTH) {
        return Promise.reject(
          `Message too long (max ${MAX_MESSAGE_LENGTH} characters).`
        );
      }
      return this.emitSocket("send-message", {
        message: trimmed,
        channel: "world",
        recipientNickname: null,
      });
    },

    sendGuildMessage(messageText) {
      const trimmed = (messageText || "").trim();
      if (!trimmed) {
        return Promise.reject("Message cannot be empty.");
      }
      if (trimmed.length > MAX_MESSAGE_LENGTH) {
        return Promise.reject(
          `Message too long (max ${MAX_MESSAGE_LENGTH} characters).`
        );
      }
      const guildStore = useGuildStore();
      if (!guildStore.membership) {
        return Promise.reject("Join a guild to unlock");
      }
      return this.emitSocket("guild_message", { message: trimmed });
    },

    sendMessage(messageText) {
      const trimmed = (messageText || "").trim();
      if (!trimmed) {
        return Promise.reject("Message cannot be empty.");
      }
      if (trimmed.length > MAX_MESSAGE_LENGTH) {
        return Promise.reject(
          `Message too long (max ${MAX_MESSAGE_LENGTH} characters).`
        );
      }

      if (trimmed.startsWith("/w ")) {
        const parts = trimmed.split(" ");
        if (parts.length < 3) {
          return Promise.reject("Invalid whisper format. Use: /w Name Message");
        }
        const recipient = parts[1];
        const msgText = parts.slice(2).join(" ");
        return this.sendWhisper(msgText, recipient);
      }

      if (trimmed.startsWith("/g ")) {
        const msgText = trimmed.slice(3).trim();
        if (!msgText) {
          return Promise.reject("Message cannot be empty.");
        }
        return this.sendGuildMessage(msgText);
      }

      if (this.activeTab === "guild") {
        return this.sendGuildMessage(trimmed);
      }
      if (this.activeTab === "personal") {
        return Promise.reject("Type /w [Name] [msg]...");
      }
      return this.sendWorldMessage(trimmed);
    },

    addMessage(msg, { fromHistory = false } = {}) {
      this.messages.push(msg);

      if (this.messages.length > HISTORY_LIMIT) {
        this.messages.splice(0, this.messages.length - HISTORY_LIMIT);
      }

      if (fromHistory) return;

      if (this.isCollapsed) {
        this.unreadCount++;
      }

      for (const tab of tabsForMessage(msg)) {
        if (this.isCollapsed || this.activeTab !== tab) {
          this.unreadByTab[tab] = (this.unreadByTab[tab] || 0) + 1;
        }
      }

      if (msg.type === "system" && msg.recipient_id != null) {
        const notificationStore = useNotificationStore();
        const raw = msg.notificationType || "info";
        const toastType = ["alert", "error", "reward", "success", "info"].includes(
          raw
        )
          ? raw
          : "info";
        notificationStore.addNotification(msg.message, toastType);
      }
    },

    toggleChat() {
      this.isCollapsed = !this.isCollapsed;
      if (!this.isCollapsed) {
        this.unreadCount = 0;
        this.clearTabUnread(this.activeTab);
      }
    },

    setActiveTab(tab) {
      const next = TABS.includes(tab) ? tab : "all";
      if (next === "guild") {
        const guildStore = useGuildStore();
        if (!guildStore.membership) return;
      }
      this.activeTab = next;
      this.clearTabUnread(next);
    },

    clearTabUnread(tab) {
      if (!this.unreadByTab[tab]) return;
      this.unreadByTab = { ...this.unreadByTab, [tab]: 0 };
    },

    leaveGuildTabIfNeeded() {
      if (this.activeTab === "guild") {
        this.activeTab = "all";
        this.clearTabUnread("all");
      }
    },
  },
});
